/**
 * Turns the raw `.DAT` row (aircraft-table.ts) into the derived spec the
 * physics runs on. Everything the game data does not store — mass, wing area,
 * the drag polar, load limits, fuel capacity — is derived here from named
 * tunables so the provenance chain stays visible: file value → documented
 * conversion → spec field.
 *
 * Two derivations carry real weight:
 *
 * 1. **The drag polar is solved, not guessed.** `CD0` is chosen so that at sea
 *    level, full throttle, level flight settles exactly on the speed
 *    flight-tunables.md §1.1 verified against the manual:
 *    `Vmax(mph) = 83.827·(power/drag) − 3.45` (r = 0.9775, RMS 2.81 mph).
 * 2. **The power lapse is fitted to the stored ceiling.** The data gives every
 *    aircraft's service ceiling exactly (offset 108 ÷ 5) and no lapse law at
 *    all, so the exponent in `P(h) = P0·σ(h)^n` is solved per aircraft to put
 *    zero excess power at that altitude (AD-007c's Open Question #3, answered
 *    the way the design note prefers: the ceilings emerge).
 */

import type { AeroSpec } from "./aero.ts";
import { barrelsForMountClass, gunBlocksOf, type GunBlockRow } from "./aircraft-gun-table.ts";
import { modelPointToBodyMetres } from "./aircraft-model-frame.ts";
import {
  DISPLAY_NAMES,
  FLEET_GUN_JAM_TABLE,
  FLEET_JAM_THRESHOLD,
  SHARED_PERFORMANCE_CLASS,
  airframeOf,
  fieldsOf,
  rudderAuthorityOf,
  targetSizeOf,
  type AircraftFields,
  type AircraftKey,
  type AirframeConfig,
} from "./aircraft-table.ts";
import type { ControlSpec, TorqueSpec, TurnSpec } from "./controls.ts";
import { limitLoadFactorFrom, type StructureSpec } from "./damage.ts";
import type { FuelSpec, PropulsionSpec } from "./engine-model.ts";
import type { GunMountSpec, GunSpec } from "./guns.ts";
import { solvePowerLapseExponent } from "./performance.ts";
import type { StallSpec } from "./spin.ts";
import { buildUndercarriage, type UndercarriageSpec } from "./undercarriage.ts";
import { DEFAULT_TUNABLES, type Tunables } from "./tunables.ts";
import {
  GRAVITY_MPS2,
  SEA_LEVEL_DENSITY_KGM3,
  bamToRadians,
  degreesToRadians,
  feetToMetres,
  mphToMps,
  rawRateToRadPerSecond,
} from "./units.ts";

export interface AircraftSpec {
  readonly key: AircraftKey;
  readonly name: string;
  readonly airframe: AirframeConfig;
  /** From aircraft.md §9 — there is no rotary flag in the game data. */
  readonly rotary: boolean;
  /**
   * True for the nine AI-only types whose (power, drag) pair is a copy-pasted
   * class default, so their derived top speed is a class figure rather than a
   * tuned one (flight-tunables.md §1.1).
   */
  readonly sharedPerformanceClass: boolean;
  readonly massKg: number;
  readonly aero: AeroSpec;
  readonly propulsion: PropulsionSpec;
  readonly fuel: FuelSpec;
  readonly control: ControlSpec;
  readonly turn: TurnSpec;
  readonly torque: TorqueSpec;
  readonly structure: StructureSpec;
  readonly stall: StallSpec;
  readonly guns: GunSpec;
  /** Wheels, skid and the limits they impose (`undercarriage.ts`). Wholly port-side — the data has no gear field. */
  readonly undercarriage: UndercarriageSpec;
  /** Top level speed at sea level, from the §1.1 ratio law — the drag polar is calibrated to hit it. */
  readonly vmaxMps: number;
  readonly ceilingM: number;
  readonly ceilingFt: number;
}

interface SpecDraft {
  readonly key: AircraftKey;
  readonly fields: AircraftFields;
  readonly tunables: Tunables;
  readonly massKg: number;
  readonly vmaxMps: number;
}

/** The §1.1 ratio law. Derived, never stored — the docs are explicit about that. */
export function ratioLawVmaxMps(power: number, drag: number, tunables: Tunables): number {
  return mphToMps(tunables.vmaxFitSlopeMph * (power / drag) + tunables.vmaxFitInterceptMph);
}

function buildAero(draft: SpecDraft): AeroSpec {
  const { fields, tunables } = draft;
  const wingAreaM2 = draft.massKg / tunables.wingLoadingKgPerM2;
  const clMax = fields.stall * tunables.clMaxPerStallUnit;
  const shaftPowerW = fields.power * tunables.wattsPerPowerUnit;

  const dynamicPressure = 0.5 * SEA_LEVEL_DENSITY_KGM3 * draft.vmaxMps * draft.vmaxMps;
  const liftCl = (draft.massKg * GRAVITY_MPS2) / (dynamicPressure * wingAreaM2);
  const thrustAtVmax = (tunables.propellerEfficiency * shaftPowerW) / draft.vmaxMps;
  const totalCd = thrustAtVmax / (dynamicPressure * wingAreaM2);
  const parasiteDrag = Math.max(
    tunables.minParasiteDrag,
    totalCd - tunables.inducedDragFactor * liftCl * liftCl,
  );

  return {
    wingAreaM2,
    clAlphaPerRad: tunables.liftCurveSlopePerRad,
    clMax,
    alphaStallRad: clMax / tunables.liftCurveSlopePerRad,
    parasiteDrag,
    inducedDragFactor: tunables.inducedDragFactor,
    postStallClFactor: tunables.postStallClFactor,
    postStallSpreadRad: tunables.postStallSpreadRad,
    stallDragPerRad: tunables.stallDragPerRad,
    slipDragPerRad2: tunables.slipDragPerRad2,
  };
}

function buildPropulsion(draft: SpecDraft, aero: AeroSpec): PropulsionSpec {
  const { tunables } = draft;
  const unlapsed: PropulsionSpec = {
    powerRatingRaw: draft.fields.power,
    shaftPowerW: draft.fields.power * tunables.wattsPerPowerUnit,
    propellerEfficiency: tunables.propellerEfficiency,
    powerLapseExponent: tunables.defaultPowerLapseExponent,
    staticThrustSpeedMps: tunables.staticThrustSpeedMps,
  };
  const powerLapseExponent = solvePowerLapseExponent(
    { massKg: draft.massKg, aero, propulsion: unlapsed },
    feetToMetres(draft.fields.ceilingFt),
    { low: tunables.minPowerLapseExponent, high: tunables.maxPowerLapseExponent },
  );
  return { ...unlapsed, powerLapseExponent };
}

function buildFuel(draft: SpecDraft): FuelSpec {
  const { tunables } = draft;
  const cruisePowerW =
    draft.fields.power * tunables.wattsPerPowerUnit * tunables.fuelCruisePowerFraction;
  return {
    capacityKg: cruisePowerW * tunables.fuelKgPerJoule * tunables.fuelEnduranceSeconds,
    kgPerJoule: tunables.fuelKgPerJoule,
  };
}

function buildControl(draft: SpecDraft): ControlSpec {
  const { fields, tunables } = draft;
  const responseGain = fields.ctlResp / tunables.controlResponseReference;
  // Roll scales with offset 42 outright (measured to 0.7% on two aircraft);
  // pitch only partly does, and `pitchRateResponseWeight` — not 1 — is what the
  // measurement supports. See that tunable's note for why the weight is small
  // and what a third aircraft would settle.
  const pitchGain = 1 + tunables.pitchRateResponseWeight * (responseGain - 1);
  return {
    responseRaw: fields.ctlResp,
    rollRateMaxRadPerSec: degreesToRadians(tunables.rollRateBaseDegPerSec * responseGain),
    pitchRateMaxRadPerSec: degreesToRadians(tunables.pitchRateBaseDegPerSec * pitchGain),
    // Offset 54 gets its *own* unit, not the turn fields' (AD-007j): a held
    // rudder on the Camel measures 4.40 °/s, which is its field-54 180 ÷ 41.
    yawRateMaxRadPerSec: rawRateToRadPerSecond(fields.yawRate, tunables.rudderDegPerSecondPerRateCount),
    rudderAuthorityRaw: rudderAuthorityOf(draft.key),
    rateLagSeconds: tunables.rateLagSeconds,
  };
}

function buildTurn(draft: SpecDraft): TurnSpec {
  const { fields, tunables } = draft;
  return {
    rightRaw: fields.turnRight,
    leftRaw: fields.turnLeft,
    rightRadPerSec: rawRateToRadPerSecond(fields.turnRight, tunables.degPerSecondPerRateCount),
    leftRadPerSec: rawRateToRadPerSecond(fields.turnLeft, tunables.degPerSecondPerRateCount),
  };
}

/**
 * Widest total arc that still counts as a fixed gun — the engine-side copy of
 * `@red-opus/formats`' `FIXED_GUN_MAX_ARC_BAM`, which the flight core cannot
 * import (it depends on no decoder). `tools/bake`'s `plane-guns.test.ts`
 * asserts the two classify all 44 records identically, so the copy cannot
 * drift.
 */
const FIXED_GUN_MAX_ARC_BAM = 2000;

/**
 * One `.DAT` gun position as a spec, with `+8`…`+14` carried through as the
 * mount's **traverse arcs** (`combat/traverse.ts` for what they are and how
 * that was settled). Two arc shapes make a mount flexible: one that wraps
 * through the rear (`horizontalFrom > horizontalTo` — 24000 → −24000 is 131.8°
 * round to 228.2°), and one that is symmetric but far wider than a fixed gun's
 * tolerance band (the bombers' nose Scarff rings).
 */
function buildGunMount(key: AircraftKey, block: GunBlockRow, index: number): GunMountSpec {
  const [rightUnits, forwardUnits, upUnits, mountClassRaw, verticalFromBam, verticalToBam, horizontalFromBam, horizontalToBam] =
    block;
  return {
    index,
    mountClassRaw,
    barrels: barrelsForMountClass(mountClassRaw),
    rightUnits,
    forwardUnits,
    upUnits,
    bodyOffsetM: modelPointToBodyMetres(key, rightUnits, forwardUnits, upUnits),
    verticalFromBam,
    verticalToBam,
    horizontalFromBam,
    horizontalToBam,
    elevationFromRad: bamToRadians(verticalFromBam),
    elevationToRad: bamToRadians(verticalToBam),
    azimuthFromRad: bamToRadians(horizontalFromBam),
    azimuthToRad: bamToRadians(horizontalToBam),
    wrapsRear: horizontalFromBam > horizontalToBam,
    flexible: horizontalFromBam > horizontalToBam || verticalToBam - verticalFromBam > FIXED_GUN_MAX_ARC_BAM,
  };
}

function buildGuns(draft: SpecDraft): GunSpec {
  const mounts = gunBlocksOf(draft.key).map((block, index) => buildGunMount(draft.key, block, index));
  const forwardMount = mounts[mounts.length - 1]!;
  return {
    positions: draft.fields.gunPositions,
    mounts,
    forwardMount,
    hasFixedGun: !forwardMount.flexible,
    barrels: forwardMount.barrels,
    damageRaw: draft.fields.gunDamage,
    jamThresholdRaw: FLEET_JAM_THRESHOLD,
    jamThresholdTable: FLEET_GUN_JAM_TABLE,
    roundsPerGun: draft.tunables.gunRoundsPerGun,
    roundsPerSecond: draft.tunables.gunRoundsPerSecond,
  };
}

function buildStructure(draft: SpecDraft): StructureSpec {
  const raw = draft.fields.wingStrength;
  return {
    wingStrengthRaw: raw === 0 ? null : raw,
    limitLoadFactor: limitLoadFactorFrom(raw, draft.tunables),
    targetSizeRaw: targetSizeOf(draft.key),
    collisionSizeRaw: draft.fields.collisionSize,
  };
}

/** Builds the full derived spec for one aircraft. */
export function buildAircraftSpec(key: AircraftKey, tunables: Tunables = DEFAULT_TUNABLES): AircraftSpec {
  const fields = fieldsOf(key);
  const draft: SpecDraft = {
    key,
    fields,
    tunables,
    massKg: fields.drag * tunables.massPerDragUnitKg,
    vmaxMps: ratioLawVmaxMps(fields.power, fields.drag, tunables),
  };
  const aero = buildAero(draft);

  return {
    key,
    name: DISPLAY_NAMES[key],
    airframe: airframeOf(key),
    rotary: fields.rotary,
    sharedPerformanceClass: SHARED_PERFORMANCE_CLASS.includes(key),
    massKg: draft.massKg,
    aero,
    propulsion: buildPropulsion(draft, aero),
    fuel: buildFuel(draft),
    control: buildControl(draft),
    turn: buildTurn(draft),
    torque: {
      rotary: fields.rotary,
      rollRaw: fields.torqueRoll,
      gyroRaw: fields.torqueGyro,
      couplingRaw: fields.rotaryCoupling,
    },
    structure: buildStructure(draft),
    stall: { liftCoefficientRaw: fields.stall, spinClassRaw: fields.spinClass },
    guns: buildGuns(draft),
    undercarriage: buildUndercarriage(key, aero, draft.massKg, tunables),
    vmaxMps: draft.vmaxMps,
    ceilingM: feetToMetres(fields.ceilingFt),
    ceilingFt: fields.ceilingFt,
  };
}
